import { Card, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import comingSoonEvent from "../../assets/lottie-files/comingSoonEvent.lottie";
import eventPic from "../../assets/markets/Tombolo.jpg";
import { useState } from "react";
import EventModal from "./EventModal";

type EventProps = {
  event: {
    id: string;
    title: string;
    shortDescription: string;
    description: string;
    dates: string[];
    displayDate: string;
    time: string;
    imageUrl: string;
    location: string;
    tickets?: string;
  };
};

export default function EventCard({ event }: EventProps) {
  const [showModal, setShowModal] = useState(false);

  // Check if all event dates are in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPast = event.dates.every((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).getTime() < today.getTime();
  });

  // Resolve image source: support absolute/public URLs, or files in src/assets via require.context
  const images = (require as any).context("../../assets", true);
  const resolveImageSrc = (path: string | undefined): string | undefined => {
    if (!path || path.length === 0) return undefined;
    const trimmed = path.trim();
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/")
    ) {
      return trimmed; // public URL or public/ path
    }
    try {
      const relative = trimmed.replace(/^.*\/assets\//, "./");
      return images(relative).default ?? images(relative);
    } catch {
      return undefined;
    }
  };

  const imgSrc = resolveImageSrc(event.imageUrl);

  const maxLength = 100;
  const shortText =
    event.shortDescription.length > maxLength
      ? event.shortDescription.substring(0, maxLength).trimEnd() + "..."
      : event.shortDescription;

  return (
    // <Col xs={12} sm={6} md={6} lg={4} className="mb-4">
    <Col xs={12} sm={6} md={6} lg={4} xl={4} className="mb-4">
      <Card
        className="h-100"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          border: "1px solid #D9D9D9",
          maxWidth: "350px",
          margin: "0 auto",
        }}
      >
        <Card.Body
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <div>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "200px",
                marginBottom: "10px",
                overflow: "hidden",
                borderRadius: "5px",
                backgroundColor: "#f3f3f3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {!imgSrc ? (
                <DotLottieReact
                  src={comingSoonEvent}
                  loop
                  autoplay
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <img
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  src={imgSrc}
                  alt={event.title}
                  onError={(e) => {
                    // Fallback to placeholder if the provided URL fails to load
                    (e.currentTarget as HTMLImageElement).src =
                      eventPic as unknown as string;
                  }}
                />
              )}
              {isPast && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0, 0, 0, 0.45)",
                      zIndex: 1,
                      borderRadius: "5px",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "0",
                      background: "linear-gradient(135deg, #dc3545, #b02a37)",
                      color: "#fff",
                      padding: "6px 16px",
                      fontSize: "0.85rem",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      borderRadius: "0 6px 6px 0",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.3)",
                      zIndex: 2,
                    }}
                  >
                    Past Event
                  </div>
                </>
              )}
            </div>

            <div style={{ textAlign: "left", marginBottom: "10px" }}>
              <Card.Title style={{ fontWeight: "bold" }}>
                {event.title}
              </Card.Title>
              <Card.Subtitle
                style={{
                  color: "#666",
                  fontSize: "0.9rem",
                  marginBottom: "0.5rem",
                }}
              >
                {event.location}
              </Card.Subtitle>
              <Card.Text>
                <div>
                  {event.displayDate}{" "}
                  {event.dates.length > 1 && (
                    <span
                      style={{
                        marginLeft: "8px",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        color: "#fff",
                        background: "linear-gradient(90deg, #FF7E5F, #FF6A00)",
                        verticalAlign: "middle",
                        display: "inline-block",
                        lineHeight: "1.5",
                      }}
                    >
                      Multiple Dates
                    </span>
                  )}
                </div>
                <div>{event.time}</div>
                {/* <div
                  style={{
                    marginTop: "0.5rem",
                    fontStyle: "italic",
                    color: "#555",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {shortText}
                  {event.shortDescription.length > maxLength && (
                    <span style={{ color: "#FF6A00", fontWeight: "bold" }}>
                      {" "}
                      See more
                    </span>
                  )}
                </div> */}
              </Card.Text>
            </div>
          </div>

          <div style={{ marginTop: "auto", textAlign: "left" }}>
            <Button
              onClick={() => setShowModal(true)}
              variant="dark"
              style={{
                backgroundColor: "#2C2C2C",
                fontFamily: "Yanone Kaffeesatz",
                fontWeight: "bold",
                width: "100%",
              }}
            >
              Learn more
            </Button>
          </div>
        </Card.Body>
      </Card>

      <EventModal
        show={showModal}
        onHide={() => setShowModal(false)}
        event={event}
      />
    </Col>
  );
}
